import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Headphones, Compass, Wrench } from 'lucide-react';
import api from '../../lib/api';

const MONTH_NAMES = {
  '01': 'Enero', '02': 'Febrero', '03': 'Marzo', '04': 'Abril',
  '05': 'Mayo', '06': 'Junio', '07': 'Julio', '08': 'Agosto',
  '09': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre',
};

const CONTENT_TYPE_CONFIG = {
  lectura: { icon: BookOpen, color: '#c9a227' },
  ajuste: { icon: Compass, color: '#7a9a5a' },
  meditacion: { icon: Headphones, color: '#8a7aaa' },
  herramienta: { icon: Wrench, color: '#b88a5a' },
};

const CampusArchive = ({ monthKey, onBack, onViewContent }) => {
  const [months, setMonths] = useState([]);
  const [monthContents, setMonthContents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (monthKey) fetchMonthContents();
    else fetchArchive();
  }, [monthKey]);

  const fetchArchive = async () => {
    try { const res = await api.get('/campus/archive'); setMonths(res.data.months || []); } catch {}
    setLoading(false);
  };

  const fetchMonthContents = async () => {
    try { const res = await api.get(`/campus/archive/${monthKey}`); setMonthContents(res.data.contents || []); } catch {}
    setLoading(false);
  };

  const formatMonth = (key) => {
    const [year, month] = key.split('-');
    return `${MONTH_NAMES[month] || month} ${year}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="campus-archive">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-[#5c4a2a]/30 hover:text-[#5c4a2a]/60 transition-colors text-sm mb-8"
          data-testid="archive-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {monthKey ? 'Volver al archivo' : 'Volver al jardín'}
        </button>

        <h1 className="text-[#5c4a2a]/70 text-2xl font-light mb-2">
          {monthKey ? formatMonth(monthKey) : 'Archivo'}
        </h1>
        <p className="text-[#5c4a2a]/30 text-sm mb-8">
          {monthKey ? 'Contenido de este mes' : 'Todo el contenido del Campus'}
        </p>

        {!monthKey ? (
          <div className="space-y-3">
            {months.length === 0 ? (
              <p className="text-[#5c4a2a]/25 text-sm text-center py-12">El archivo está vacío por ahora</p>
            ) : (
              months.map((m) => (
                <button
                  key={m.month_key}
                  onClick={() => onViewContent ? null : null}
                  className="w-full bg-white hover:bg-white border border-[#5c4a2a]/[0.06] hover:border-[#c9a227]/20 rounded-xl px-5 py-4 flex items-center justify-between transition-all hover:shadow-sm"
                  data-testid={`archive-month-${m.month_key}`}
                >
                  <div className="text-left">
                    <h3 className="text-[#5c4a2a]/70 text-sm">{formatMonth(m.month_key)}</h3>
                    <p className="text-[#5c4a2a]/30 text-xs mt-1">
                      {m.total_contents} contenidos · {m.read_count} leídos
                    </p>
                  </div>
                  {m.read_count < m.total_contents && (
                    <div className="w-2 h-2 bg-[#c9a227] rounded-full animate-pulse" />
                  )}
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {monthContents.map((item) => {
              const config = CONTENT_TYPE_CONFIG[item.content_type] || CONTENT_TYPE_CONFIG.lectura;
              const Icon = config.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewContent(item.id)}
                  className="w-full flex items-center gap-4 bg-white hover:bg-white border border-[#5c4a2a]/[0.06] hover:border-[#c9a227]/20 rounded-xl px-5 py-4 transition-all hover:shadow-sm"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${config.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <h3 className="text-[#5c4a2a]/70 text-sm truncate">{item.title}</h3>
                  </div>
                  {!item.is_read && <div className="w-2 h-2 bg-[#c9a227] rounded-full animate-pulse flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusArchive;
